import type { TravelerDocumentMatchDto } from '@bandido/shared/dtos';
import {
  isLookupDocumentNumber,
  normalizeDocumentNumber,
} from '@bandido/shared/helpers';

import type { LinkCaseTravelerByDocumentVariables } from './types';

const MIN_DOCUMENT_LOOKUP_DIGITS = 4;
/** Países cuyo documento nacional se llama DNI. */
const NATIONAL_ID_AS_DNI = new Set(['AR', 'PE', 'ES']);

// Una sola definición, compartida con el backend.
export { isLookupDocumentNumber, normalizeDocumentNumber };

/**
 * En el buscador libre, un texto se trata como documento cuando además de ser
 * buscable tiene 4 dígitos: "4.512.783-9" y "C1234567" sí; "Ana Pérez" no.
 * En un campo que ya es de documento alcanza con `isLookupDocumentNumber`.
 */
export const isDocumentLookup = (value: string): boolean =>
  isLookupDocumentNumber(value) &&
  (normalizeDocumentNumber(value).match(/\d/g)?.length ?? 0) >=
    MIN_DOCUMENT_LOOKUP_DIGITS;

/** Con letras suele ser un pasaporte; sólo dígitos, el documento nacional. */
export const suggestedDocumentType = (
  value: string,
  countryCode?: string,
): string => {
  if (/[A-Z]/.test(normalizeDocumentNumber(value))) return 'passport';
  return NATIONAL_ID_AS_DNI.has(countryCode ?? '') ? 'dni' : 'ci';
};

export const sameDocumentNumber = (left: string, right: string): boolean =>
  normalizeDocumentNumber(left) === normalizeDocumentNumber(right);

/** Coincidencias que no son la ficha que ya se está usando o editando. */
export const otherTravelerMatches = (
  matches: TravelerDocumentMatchDto[] | undefined,
  excludedTravelerIds: readonly string[] = [],
): TravelerDocumentMatchDto[] =>
  (matches ?? []).filter(
    (match) => !excludedTravelerIds.includes(match.travelerId),
  );

/**
 * Usar una ficha encontrada desde el formulario. Al editar otra ficha, la
 * encontrada la reemplaza en el roster en la misma operación del servidor.
 */
export const linkVariablesForMatch = (
  match: TravelerDocumentMatchDto,
  documentNumber: string,
  editingTravelerId?: string,
): LinkCaseTravelerByDocumentVariables => ({
  travelerId: match.travelerId,
  documentNumber,
  ...(editingTravelerId && editingTravelerId !== match.travelerId
    ? { replacesTravelerId: editingTravelerId }
    : {}),
});
